import * as React from 'react';

import { Grid } from '@coursera/cds-core';

import AdminRightNavGridItem from 'bundles/composable-page-header-admin/components/AdminRightNavGridItem';
import { useAdminMyPageHeader } from 'bundles/composable-page-header-admin/contexts/AdminMyPageHeaderContext';
import {
  getEnterpriseLogoLabel,
  getEnterpriseOrgLogo,
  getEnterpriseOrgLogoLabel,
  getEnterpriseProgramLogos,
} from 'bundles/composable-page-header-enterprise/utils/enterpriseLogoUtils';
import MainNavigationWrapper from 'bundles/composable-page-header/components/MainNavigationWrapper';
import NotificationsLegacyGridItem from 'bundles/composable-page-header/components/grid-items/NotificationsLegacyGridItem';
import CoachStudioGridItem, {
  showCoachStudio,
} from 'bundles/composable-page-header/components/grid-items/coach/CoachStudioGridItem';
import CourseraHomeLogo from 'bundles/composable-page-header/components/grid-items/home-logo/CourseraHomeLogo';
import DefaultLogoImage from 'bundles/composable-page-header/components/grid-items/home-logo/DefaultLogoImage';
import HomeLogoGridItemModule from 'bundles/composable-page-header/components/grid-items/home-logo/HomeLogoGridItemModule';
import DefaultAccountDropdownGridItemRenderer from 'bundles/composable-page-header/module-renderers/DefaultAccountDropdownGridItemRenderer';
import GrowthDiscoveryEpicClient from 'bundles/epic/clients/GrowthDiscovery';

import _t from 'i18n!nls/composable-page-header-admin';

const LoggedInAdminDesktopMainNav = () => {
  const {
    chosenEnterpriseProgram,
    chosenDegree,
    naptimeChosenOrganization,
    naptimeOrganizationsWithProgramEnrollments,
    enrolledDegreePrograms,
    hasEnrolledPrograms,
    naptimeManagedThirdPartyOrganizations,
    hasCheatingIncidents,
    canViewEducatorAdminLink,
    isCourseraPlusLearner,
    courseraPlusSubscriptionCycle,
  } = useAdminMyPageHeader();

  const isOrgScoped = GrowthDiscoveryEpicClient.preview('orgScopedHome');

  const getHeaderLogos = (): React.ReactNode[] => {
    let logos: React.ReactNode[] = [<CourseraHomeLogo key="1" />];

    if (isOrgScoped && naptimeChosenOrganization) {
      logos.push(getEnterpriseOrgLogo(naptimeChosenOrganization));
    } else if (!isOrgScoped && chosenEnterpriseProgram) {
      logos = [...logos, ...getEnterpriseProgramLogos(chosenEnterpriseProgram)];
    } else if (chosenDegree) {
      logos.push(
        <DefaultLogoImage
          key={`${chosenDegree?.partners[0]?.id || 0}_header_logo_image`}
          src={chosenDegree?.partners[0]?.rectangularLogo || ''}
          alt={chosenDegree?.partners[0]?.name || ''}
        />
      );
    }

    return logos;
  };

  const getHeaderAriaLabel = (): string | undefined => {
    if (isOrgScoped && naptimeChosenOrganization) {
      return getEnterpriseOrgLogoLabel(naptimeChosenOrganization);
    } else if (!isOrgScoped && chosenEnterpriseProgram) {
      return getEnterpriseLogoLabel(chosenEnterpriseProgram);
    } else if (chosenDegree) {
      _t('#{degreeName} home page | Coursera', { degreeName: chosenDegree?.name || 'Degree' });
    } else {
      return undefined;
    }
    return undefined;
  };

  return (
    <MainNavigationWrapper fullWidth>
      <Grid item md={4} lg={5} container alignItems="center" spacing={16}>
        <Grid item>
          {/* Home link on these pages are for admin only, so hardcoding /admin link here */}
          <HomeLogoGridItemModule href="/admin" logos={getHeaderLogos()} ariaLabel={getHeaderAriaLabel()} />
        </Grid>
      </Grid>
      <Grid item container md={8} lg={7} justifyContent="flex-end" alignItems="center" wrap="nowrap">
        <AdminRightNavGridItem />
        <Grid item>
          <NotificationsLegacyGridItem />
        </Grid>
        {showCoachStudio() && (
          <Grid item>
            <CoachStudioGridItem />
          </Grid>
        )}
        <Grid item>
          <DefaultAccountDropdownGridItemRenderer
            hasEnrolledPrograms={!!hasEnrolledPrograms}
            naptimeManagedThirdPartyOrganizations={naptimeManagedThirdPartyOrganizations}
            hasCheatingIncidents={!!hasCheatingIncidents}
            canViewEducatorAdminLink={!!canViewEducatorAdminLink}
            isCourseraPlusLearner={!!isCourseraPlusLearner}
            courseraPlusSubscriptionCycle={courseraPlusSubscriptionCycle}
            currentOrganization={naptimeChosenOrganization}
            organizations={naptimeOrganizationsWithProgramEnrollments}
            degrees={enrolledDegreePrograms}
            currentDegree={chosenDegree}
          />
        </Grid>
      </Grid>
    </MainNavigationWrapper>
  );
};

export default LoggedInAdminDesktopMainNav;
